import { axios, baseUrlForService, blueApi } from '@/axios.config';
import config from '@/config';
import * as base64 from '@/lib/base64';
import moment from 'moment/moment';
import Vue from 'vue';
import { debounce } from '@/lib/tools';

import { parseGrpcData } from '@alphauslabs/grpc-chunk-parser';

export function hash({ owner, ownerId, dimension, from, to, type, vendor }) {
  from = moment(from).format('YYYY-MM-DD');
  to = moment(to).format('YYYY-MM-DD');
  return `${owner}-${ownerId ? `${ownerId}-` : ''}${dimension}-${from}-${to}-${type}-${vendor}`;
}

export const state = {
  reports: {},
  reportCsv: {},
  requests: {},
  selectedMonth: '',
  logtime: 0,
};

export const actions = {
  fetchReports({ state, rootState, commit, dispatch, rootGetters }, usageReport) {
    const reqHash = hash(usageReport);

    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    let { resourceType, owner, ownerId, dimension, from, to, vendor, type } = usageReport;

    if (config.blueApi.canFetchData(rootState.userState.companyId, vendor, owner)) {
      from = moment(from);
      to = moment(to);

      let url = '';
      let params = {};
      if (resourceType === 'account' || resourceType === 'azure_subscription') {
        url = `/cost/v1/${vendor}/costs:read`;
        params = {
          startTime: from.format('YYYYMMDD'),
          endTime: to.format('YYYYMMDD'),
        };
        switch (vendor) {
          case 'aws':
            params.awsOptions = {
              groupByColumns:
                'account,productCode,region,zone,instanceType,usageType,operation,description',
              groupByMonth: true,
            };
            break;
          case 'azure':
            params.azureOptions = {
              groupByColumns:
                'account,date,serviceName,region,description,billableQuantity,effectiveUnitPrice,instanceType,usageType,operation',
              groupByMonth: true,
            };
            break;
        }
        if (owner === 'report_filter') {
          const activeFilterId = ownerId;
          const filterData = rootGetters['reportFilters/getActiveFilterData']({
            vendor: rootState.vendor,
            filterId: activeFilterId,
          });
          const { accountId, groupId, awsOptions } = filterData;
          url = `cost/v1/${vendor}/costs:read`;
          params.awsOptions = {
            ...awsOptions,
            tagFilters: awsOptions.includeTags ? awsOptions.tagFilters : [],
          };
          params.groupId = groupId;
          params.accountId = accountId;
        } else if (owner === 'company') {
          params.groupId = rootState.userState.companyId;
        } else if (ownerId) {
          switch (vendor) {
            case 'aws':
              params.accountId = ownerId.split('|')[0];
              break;
            case 'azure':
              params.accountId = ownerId.split('|')[0];
              break;
          }
        }
      }
      if (resourceType === 'tag') {
        if (ownerId == 'nontag') {
          url = `/cost/v1/${vendor}/nontagcosts:read`;
        } else {
          url = `/cost/v1/${vendor}/tagcosts:read`;
        }
        params = {
          billingInternalId: rootState.userState.companyId,
          startTime: from.format('YYYYMMDD'),
          endTime: to.format('YYYYMMDD'),
          groupByMonths: true,
          awsOptions: {
            tagId: ownerId,
          },
        };
      }

      const cancelToken = new blueApi.CancelToken(() => {});
      commit('fetchStart', { usageReport, reqHash, cancelToken });

      return blueApi
        .post(url, params)
        .then(({ data }) => {
          // Check and convert response data:
          // In case data only has 1 row, it will be converted to object type automatically,
          // so need to check data type before adding it to record array.
          let records = [];
          if (typeof data === 'string' || data instanceof String) {
            records.push(...data.split('\n'));
          } else {
            records.push(JSON.stringify(data));
          }
          let graphData = {};
          graphData[vendor] = generateReports(vendor, records);
          commit('fetchSucceed', { reqHash });
          commit('updateUsageReport', { data: graphData, vendor, reqHash });
        })
        .catch(err => {
          if (blueApi.isCancel(err)) {
            commit('fetchCanceled', { reqHash });
            return;
          }

          commit('fetchFailed', { reqHash, err });
          throw err;
        });
    } else {
      let url = `bill/${owner}/${ownerId}/${dimension}`;
      let params = {
        from: from,
        to: to,
        vendor: vendor,
        type: type,
      };
      const cancelToken = new axios.CancelToken(() => {});
      commit('fetchStart', { usageReport, reqHash, cancelToken });

      return axios
        .get(url, { params: params })
        .then(({ data }) => {
          commit('fetchSucceed', { reqHash });
          commit('updateUsageReport', { data, vendor, reqHash });

          switch (vendor) {
            case 'azure':
              dispatch('billingReports/azure/fetchReportCsv', usageReport, { root: true });
              break;
            case 'gcp':
            case 'aws':
              dispatch('fetchReportCsv', usageReport);
          }
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            commit('fetchCanceled', { reqHash });
            return;
          }

          commit('fetchFailed', { reqHash, err });
          throw err;
        });
    }
  },

  fetchTagReports({ state, rootState, commit }, usageReport) {
    const reqHash = hash(usageReport);

    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    let { ownerId, from, to, vendor } = usageReport;
    const url = `/cost/v1/${vendor}/tagcosts:read`;
    const params = {
      billingInternalId: rootState.companyId,
      startTime: moment(from).format('YYYYMMDD'),
      endTime: moment(to).format('YYYYMMDD'),
      groupByMonths: true,
      awsOptions: {
        tagId: ownerId,
      },
    };

    if (!ownerId) {
      delete params.awsOptions;
    }

    const cancelToken = new blueApi.CancelToken(() => {});
    commit('fetchStart', { usageReport, reqHash, cancelToken });

    return blueApi
      .post(url, params)
      .then(({ data }) => {
        commit('fetchSucceed', { reqHash });
        let records = [];
        if (typeof data === 'string' || data instanceof String) {
          records = data.split('\n');
        } else {
          records = [JSON.stringify(data)];
        }
        const reportData = {};
        reportData[vendor] = generateReports(vendor, records);
        commit('updateUsageReport', { data: reportData, vendor, reqHash });
      })
      .catch(err => {
        if (blueApi.isCancel(err)) {
          commit('fetchCanceled', { reqHash });
          return;
        }

        commit('fetchFailed', { reqHash, err });
        throw err;
      });
  },

  fetchNonTagReports({ state, rootState, commit }, usageReport) {
    const reqHash = hash(usageReport);
    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    let { from, to, vendor } = usageReport;
    const url = `/cost/v1/${vendor}/nontagcosts:read`;
    const params = {
      billingInternalId: rootState.companyId,
      startTime: moment(from).format('YYYYMMDD'),
      endTime: moment(to).format('YYYYMMDD'),
      groupByMonths: true,
    };

    const cancelToken = new blueApi.CancelToken(() => {});
    commit('fetchStart', { usageReport, reqHash, cancelToken });

    return blueApi
      .post(url, params)
      .then(({ data }) => {
        commit('fetchSucceed', { reqHash });
        let records = [];
        if (typeof data === 'string' || data instanceof String) {
          records = data.split('\n');
        } else {
          records = [JSON.stringify(data)];
        }
        const reportData = {};
        reportData[vendor] = generateReports(vendor, records);
        commit('updateUsageReport', { data: reportData, vendor, reqHash });
      })
      .catch(err => {
        if (blueApi.isCancel(err)) {
          commit('fetchCanceled', { reqHash });
          return;
        }

        commit('fetchFailed', { reqHash, err });
        throw err;
      });
  },

  fetchCompanyReports({ state, rootState, commit }, usageReport) {
    const reqHash = hash(usageReport);

    if (state.requests[reqHash] && state.requests[reqHash].inProgress) {
      return;
    }

    let { from, to, vendor, resourceTypes } = usageReport;
    const cancelToken = new blueApi.CancelToken(() => {});
    commit('fetchStart', { usageReport, reqHash, cancelToken });

    const allRecords = [];

    const fetchData = async (url, params, limit, allRecords) => {
      // The UI will wait for the debounced function to finish before updating the UI
      const updateUI = debounce(
        ({ data, vendor, reqHash }) => commit('updateUsageReport', { data, vendor, reqHash }),
        500
      );
      parseGrpcData(
        {
          url: baseUrlForService('blue') + url,
          method: 'POST',
          headers: {
            Authorization: blueApi.defaults.headers.common['Authorization'],
          },
          body: params,
        },
        {
          limiter: limit,
          concatData: false,
          showDebug: false,
        },
        async data => {
          allRecords.push(...data);
          const reportData = {};
          reportData[vendor] = generateCompanyCrossTagReportsV2(vendor, allRecords);
          // commit('updateUsageReport', { data: reportData, vendor, reqHash });
          updateUI({ data: reportData, vendor, reqHash });
        },
        async finalData => {
          commit('fetchSucceed', { reqHash });
          console.log('finalData length: ', finalData.length);
        },
        err => {
          commit('requestFailed', { reqHash, err });
          throw err;
        }
      );
    };

    if (resourceTypes.includes('account')) {
      const accountParams = {
        groupId: rootState.companyId,
        startTime: moment(from).format('YYYYMMDD'),
        endTime: moment(to).format('YYYYMMDD'),
        awsOptions: {
          groupByColumns:
            'account,productCode,serviceCode,region,zone,usageType,instanceType,operation,invoiceId,description,resourceId',
          groupByMonth: false,
        },
      };

      // costs read
      const url = `/cost/v1/${vendor}/costs:read`;
      fetchData(url, accountParams, 250, allRecords);
    }

    if (resourceTypes.includes('tag')) {
      const tagParams = {
        billingInternalId: rootState.companyId,
        startTime: moment(from).format('YYYYMMDD'),
        endTime: moment(to).format('YYYYMMDD'),
        groupByMonths: true,
      };

      // tag cost
      const tagUrl = `/cost/v1/${vendor}/tagcosts:read`;
      fetchData(tagUrl, tagParams, 150, allRecords);

      // non tag cost
      const nonTagUrl = `/cost/v1/${vendor}/nontagcosts:read`;
      fetchData(nonTagUrl, tagParams, 150, allRecords);
    }

    return Promise.resolve();
  },

  fetchReportCsv({ state, commit, rootState }, usageReport) {
    // TODO:共有ファンクションにまとめる
    const type = plan => {
      if (plan === 'limited3' || plan === 'registered') {
        return 'original';
      }
      return 'mobingi';
    };

    const reqHash = hash(usageReport);
    let { owner, ownerId, from } = usageReport;

    if (owner === 'group' || owner === 'taggroup') {
      return;
    }

    let url, params;
    if (owner === 'account') {
      url = `report/csv`;
      params = {
        month: moment(from).format('YYYY-MM'),
        customer_id: ownerId.split('|')[0],
        type: type(rootState.userState.plan.type),
        vendor: rootState.vendor,
      };
    }
    if (owner === 'tag') {
      const decodedOwnerId = atob(ownerId);
      const tagName = decodedOwnerId.split('|')[0];

      url = `tag/csv`;
      params = {
        // month: '2019-01',
        month: moment(from).format('YYYY-MM'),
        tag_name: tagName,
        vendor: rootState.vendor,
      };
    }

    return new Promise((resolve, reject) => {
      clearInterval(state.csvDownloadTimerId);
      const fetch = () => {
        return axios
          .get(url, { params: params })
          .then(({ data }) => {
            commit('updateReportCsv', { data, reqHash });
            resolve();
          })
          .catch(err => {
            if (axios.isCancel(err)) {
              return;
            }
            reject(err);
          });
      };

      fetch();

      const timerId = setInterval(fetch, 540000);
      commit('csvDownloadTimer', timerId);
    });
  },
};

export const mutations = {
  updateLogtime(state, val) {
    state.logtime = val;
  },
  updateUsageReport(state, { data, vendor, reqHash }) {
    const reportData = data[vendor];
    Vue.set(state.reports, reqHash, reportData);
  },

  updateReportCsv(state, { data, reqHash }) {
    Vue.set(state.reportCsv, reqHash, data);
  },

  updateSelectedMonth(state, month) {
    state.selectedMonth = month;
  },

  fetchStart(state, { reqHash, cancelToken }) {
    Vue.set(state.requests, reqHash, {
      inProgress: true,
      error: null,
      canceled: false,
      cancelToken,
    });
  },

  fetchSucceed(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: false,
      cancelToken: null,
    });
  },

  fetchCanceled(state, { reqHash }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: null,
      canceled: true,
      cancelToken: null,
    });
  },

  fetchFailed(state, { reqHash, err }) {
    Vue.set(state.requests, reqHash, {
      inProgress: false,
      error: err,
      canceled: false,
      cancelToken: null,
    });
  },

  csvDownloadTimer(state, timerId) {
    Vue.set(state, 'csvDownloadTimerId', timerId);
  },
};

export const modules = {
  azure: {
    namespaced: true,
    mutations: {
      updateReportCsv(state, { data, reqHash }) {
        Vue.set(this.state.billingReports.reportCsv, reqHash, data);
      },
    },
    actions: {
      fetchReportCsv({ commit }, usageReport) {
        const reqHash = hash(usageReport);
        const { owner, ownerId, from } = usageReport;
        if (owner === 'group' || owner === 'tag' || owner === 'taggroup') {
          return;
        }
        const pathVariables = {
          month: moment(from).format('YYYY-MM'),
          customer_id: ownerId.split('|')[0],
        };
        const url = `azure/v3/report/period/${pathVariables.month}/customer/${pathVariables.customer_id}`;
        return axios
          .get(url)
          .then(({ data }) => {
            if (data.payload.link.file_found) {
              commit('updateReportCsv', { data: data.payload.link.link, reqHash });
            } else {
              commit('updateReportCsv', { data: 'unavailable', reqHash });
            }
            return data;
          })
          .catch(err => {
            if (axios.isCancel(err)) {
              return;
            }
            throw err;
          });
      },
    },
  },
};

export const getters = {
  /**
   * log interval
   * @param state
   * @return {integer}
   */
  getLogtimeInterval(state) {
    return state.logtime ? Date.now() - state.logtime : 0;
  },
};

// Generate reports following UI display format from records.
const generateReports = (vendor, records) => {
  let services = [];
  for (const record of records) {
    if (!record) {
      continue;
    }
    const recordJson = JSON.parse(record);
    if (!recordJson) {
      continue;
    }
    const cost = recordJson.result[vendor];
    if (!cost) {
      continue;
    }
    services.push(cost);
  }
  if (services.length === 0) {
    return [];
  }

  // Group services by service code and sum up the total cost.
  const serviceMap = {};
  for (const idx in services) {
    const service = services[idx];

    let code = '';
    let rep = {};
    switch (vendor) {
      case 'aws': {
        const usage = Math.round(service.usage * 100000) / 100000;
        rep = {
          account: service.account,
          availability_zone: service.zone,
          description: service.description,
          instance_type: service.instanceType,
          operation: service.operation,
          region: service.region,
          usage_type: service.usageType,
          usage_quantity: usage,
          cost: service.cost,
          id: service.tagId,
        };
        code = service.serviceCode ? service.serviceCode : service.productCode;
        break;
      }
      case 'azure': {
        const billableQuantity = Math.round(service.billableQuantity * 100000) / 100000;
        rep = {
          account: service.account,
          date: service.date,
          service_name: service.serviceName,
          region: service.region,
          description: service.description,
          usage_quantity: billableQuantity,
          unit_price: service.effectiveUnitPrice,
          cost: service.targetCost,
          currency: service.targetCurrency,
          instance_type: service.instanceType,
          usage_type: service.usageType,
          operation: service.operation,
        };
        code = service.serviceName;
        break;
      }
    }
    var newService = serviceMap[code];
    if (!newService) {
      newService = {
        service: code,
        total: service.cost,
        report: [rep],
      };
    } else {
      newService.total += service.cost;
      newService.report.push(rep);
    }
    serviceMap[code] = newService;
  }

  // Convert report map to array and set it to service's report list.
  services = [];
  for (const skey in serviceMap) {
    const service = serviceMap[skey];
    service.report.sort((a, b) => {
      if (a.account !== b.account) {
        return a.account.localeCompare(b.account);
      }
      const left = '' + (a.instance_type ? '_' + a.instance_type : '') + a.usage_type + a.operation;
      const right =
        '' + (b.instance_type ? '_' + b.instance_type : '') + b.usage_type + b.operation;
      return left.localeCompare(right);
    });
    services.push(service);
  }

  return services;
};

// Unused code
// const generateCompanyAccountReports = (vendor, records) => {
//   const repGrps = [];
//   const grpMap = {};
//
//   // Group records by accountId.
//   for (const record of records) {
//     if (!record) {
//       continue;
//     }
//     const recordJson = JSON.parse(record.toString());
//     if (!recordJson) {
//       continue;
//     }
//     const cost = recordJson.result[vendor];
//     if (!cost) {
//       continue;
//     }
//
//     const grpKey = cost.account;
//     let grp = grpMap[grpKey];
//     if (!grp) {
//       const dt = new Date(cost.date);
//       grp = {
//         customer_id: cost.account,
//         customer_name: cost.account,
//         month: `${dt.getFullYear()}-${dt.getMonth() + 1}`,
//         services: [cost],
//       };
//     } else {
//       grp.services.push(cost);
//     }
//     grpMap[grpKey] = grp;
//   }
//
//   // Group services of each account group.
//   if (Object.keys(grpMap).length === 0) {
//     return [];
//   }
//   for (const gkey in grpMap) {
//     const grp = grpMap[gkey];
//     const svcMap = {};
//     const svcs = [];
//
//     // Group services by service code and sum up the total cost.
//     for (const idx in grp.services) {
//       const svc = grp.services[idx];
//       const usage = Math.round(svc.usage * 100000) / 100000;
//       const rep = {
//         availability_zone: svc.zone,
//         description: svc.description,
//         instance_type: svc.instanceType,
//         operation: svc.operation,
//         region: svc.region,
//         usage_type: svc.usageType,
//         usage_quantity: usage,
//         cost: svc.cost,
//         resource_id: svc.resourceId,
//       };
//       const code = svc.serviceCode ? svc.serviceCode : svc.productCode;
//       var newSvc = svcMap[code];
//       if (!newSvc) {
//         newSvc = {
//           service: code,
//           total: svc.cost,
//           report: [rep],
//         };
//       } else {
//         newSvc.total += svc.cost;
//         newSvc.report.push(rep);
//       }
//       svcMap[code] = newSvc;
//     }
//
//     // Convert report map to array and set it to service's report list.
//     for (const skey in svcMap) {
//       const svc = svcMap[skey];
//       svc.report.sort((a, b) => {
//         const left =
//           '' + (a.instance_type ? '_' + a.instance_type : '') + a.usage_type + a.operation;
//         const right =
//           '' + (b.instance_type ? '_' + b.instance_type : '') + b.usage_type + b.operation;
//         return left.localeCompare(right);
//       });
//       svcs.push(svc);
//     }
//
//     grp.services = svcs;
//     repGrps.push(grp);
//   }
//
//   // Sort report groups.
//   repGrps.sort((a, b) => {
//     const left = a.customer_name;
//     const right = b.customer_name;
//     return left.localeCompare(right);
//   });
//
//   return repGrps;
// };

// Unused code
// const generateCompanyCrossTagReports = (vendor, records) => {
//   const repGrps = [];
//   const grpMap = {};
//
//   // Group records by tagId.
//   for (const record of records) {
//     if (!record) {
//       continue;
//     }
//     const cost = JSON.parse(record).result[vendor];
//     if (!cost) {
//       continue;
//     }
//
//     const grpKey = cost.tagId ? cost.tagId : `nontag${cost.account}`;
//     let grp = grpMap[grpKey];
//     if (!grp) {
//       const dt = new Date(cost.date);
//       grp = {
//         customer_id: '',
//         month: `${dt.getFullYear()}-${dt.getMonth() + 1}`,
//         services: [cost],
//       };
//       if (cost.tagId) {
//         const decodedTagId = base64.decode(cost.tagId);
//         const tagObj = JSON.parse(decodedTagId);
//         const kv = tagObj.tags[0];
//
//         grp.tag_key = kv.key;
//         grp.tag_value = kv.value;
//         grp.customer_name = `${kv.key}: ${kv.value} (${cost.account})`;
//       } else {
//         grp.tag_key = 'nontag';
//         grp.tag_value = '';
//         grp.customer_name = `Non tag (${cost.account})`;
//       }
//     } else {
//       grp.services.push(cost);
//     }
//     grpMap[grpKey] = grp;
//   }
//
//   // Group services of each tag group.
//   if (Object.keys(grpMap).length === 0) {
//     return [];
//   }
//   for (const gkey in grpMap) {
//     const grp = grpMap[gkey];
//     const svcMap = {};
//     const svcs = [];
//
//     // Group services by service code and sum up the total cost.
//     for (const idx in grp.services) {
//       const svc = grp.services[idx];
//       const usage = Math.round(svc.usage * 100000) / 100000;
//       const rep = {
//         availability_zone: svc.zone,
//         description: svc.description,
//         instance_type: svc.instanceType,
//         operation: svc.operation,
//         region: svc.region,
//         usage_type: svc.usageType,
//         usage_quantity: usage,
//         cost: svc.cost,
//         resource_id: svc.resourceId,
//       };
//       const code = svc.serviceCode ? svc.serviceCode : svc.productCode;
//       var newSvc = svcMap[code];
//       if (!newSvc) {
//         newSvc = {
//           service: code,
//           total: svc.cost,
//           report: [rep],
//         };
//       } else {
//         newSvc.total += svc.cost;
//         newSvc.report.push(rep);
//       }
//       svcMap[code] = newSvc;
//     }
//
//     // Convert report map to array and set it to service's report list.
//     for (const skey in svcMap) {
//       const svc = svcMap[skey];
//       svc.report.sort((a, b) => {
//         const left =
//           '' + (a.instance_type ? '_' + a.instance_type : '') + a.usage_type + a.operation;
//         const right =
//           '' + (b.instance_type ? '_' + b.instance_type : '') + b.usage_type + b.operation;
//         return left.localeCompare(right);
//       });
//       svcs.push(svc);
//     }
//
//     grp.services = svcs;
//     repGrps.push(grp);
//   }
//
//   // Sort report groups.
//   repGrps.sort((a, b) => {
//     if (a.tag_key === 'nontag') {
//       return 1;
//     }
//     if (b.tag_key === 'nontag') {
//       return -1;
//     }
//     const left = '' + a.tag_key + a.tag_value;
//     const right = '' + b.tag_key + b.tag_value;
//     return left.localeCompare(right);
//   });
//
//   return repGrps;
// };

const generateCompanyCrossTagReportsV2 = (vendor, records) => {
  const repGrps = [];
  const grpMap = {};

  // Group records by tagId.
  for (let i = 0, len = records.length; i < len; i++) {
    const record = records[i];
    if (!record) {
      continue;
    }
    const cost = record[vendor];
    if (!cost) {
      continue;
    }

    const grpKey = cost.tagId ? cost.tagId : `nontag${cost.account}`;
    let grp = grpMap[grpKey];
    if (!grp) {
      const dt = new Date(cost.date);
      grp = {
        customer_id: '',
        month: `${dt.getFullYear()}-${dt.getMonth() + 1}`,
        services: [cost],
      };
      if (cost.tagId) {
        const decodedTagId = base64.decode(cost.tagId);
        const tagObj = JSON.parse(decodedTagId);
        const kv = tagObj.tags[0];

        grp.tag_key = kv.key;
        grp.tag_value = kv.value;
        grp.customer_name = `${kv.key}: ${kv.value} (${cost.account})`;
      } else {
        grp.tag_key = 'nontag';
        grp.tag_value = '';
        grp.customer_name = `Non tag (${cost.account})`;
      }
    } else {
      grp.services.push(cost);
    }
    grpMap[grpKey] = grp;
  }

  // Group services of each tag group.
  if (Object.keys(grpMap).length === 0) {
    return [];
  }
  for (const gkey in grpMap) {
    const grp = grpMap[gkey];
    const svcMap = {};
    const svcs = [];

    // Group services by service code and sum up the total cost.
    for (let i = 0, len = grp.services.length; i < len; i++) {
      const svc = grp.services[i];
      if (!svc || !svc.cost) {
        continue;
      }
      const usage = Math.round(svc.usage * 100000) / 100000;
      const rep = {
        availability_zone: svc.zone,
        description: svc.description,
        instance_type: svc.instanceType,
        operation: svc.operation,
        region: svc.region,
        usage_type: svc.usageType,
        usage_quantity: usage,
        cost: svc.cost,
        resource_id: svc.resourceId,
      };
      const code = svc.serviceCode ? svc.serviceCode : svc.productCode;
      let newSvc = svcMap[code];
      if (!newSvc) {
        newSvc = {
          service: code,
          total: svc.cost,
          report: [rep],
        };
        svcMap[code] = newSvc;
      } else {
        newSvc.total += svc.cost;
        newSvc.report.push(rep);
      }
    }

    // Convert report map to array and set it to service's report list.
    for (const skey in svcMap) {
      const svc = svcMap[skey];
      svc.report.sort((a, b) => {
        const left =
          '' + (a.instance_type ? '_' + a.instance_type : '') + a.usage_type + a.operation;
        const right =
          '' + (b.instance_type ? '_' + b.instance_type : '') + b.usage_type + b.operation;
        return left.localeCompare(right);
      });
      svcs.push(svc);
    }

    grp.services = svcs;
    repGrps.push(grp);
  }

  // Sort report groups.
  repGrps.sort((a, b) => {
    if (a.tag_key === 'nontag') {
      return 1;
    }
    if (b.tag_key === 'nontag') {
      return -1;
    }
    const left = '' + a.tag_key + a.tag_value;
    const right = '' + b.tag_key + b.tag_value;
    return left.localeCompare(right);
  });

  return repGrps;
};
